"use client";

import { useState } from "react";
import Link from "next/link";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { SearchBar } from "@/components/ui/SearchBar";
import { FilterPanel, INITIAL_FILTERS, type FilterState } from "@/components/ui/FilterPanel";
import { parseWalkMinutes, convertWarekiToYear, parseFacilities } from "@/lib/utils/filters";
import { rentLabel } from "@/lib/constants/jkk-housing-type";

interface DanchiItem {
  id: string;
  slug: string;
  name: string;
  vacancyCount: number;
  housingType?: string | null;
  href?: string;
  address?: string | null;
  rentMin?: number | null;
  rentMax?: number | null;
  access?: string | null;
  prefectureName?: string | null;
  regionId?: string | null;
  prefectureId?: string | null;
  builtYear?: string | null;
  facilities?: string | null;
  structure?: string | null;
  lastCrawledAt?: string | Date | null;
}

interface Props {
  items: DanchiItem[];
  linkPrefix?: string;
  showPrefecture?: boolean;
  showNoVacancy?: boolean;
  initialQuery?: string;
  detailed?: boolean;
  enableFilters?: boolean;
}

function buildLink(item: DanchiItem, linkPrefix?: string) {
  if (item.href) return item.href;
  if (linkPrefix) return `${linkPrefix}/${item.slug}`;
  return `/ur/area/${item.regionId}/${item.prefectureId}/${item.slug}`;
}

function matchesQuery(item: DanchiItem, query: string): boolean {
  const q = query.toLowerCase();
  if (item.name.toLowerCase().includes(q)) return true;
  if (item.address?.toLowerCase().includes(q)) return true;
  if (item.access?.toLowerCase().includes(q)) return true;
  if (item.prefectureName?.toLowerCase().includes(q)) return true;
  return false;
}

function matchesFilters(item: DanchiItem, filters: FilterState): boolean {
  if (filters.vacancyOnly && item.vacancyCount === 0) return false;

  if (filters.builtWithinYears != null) {
    const year = convertWarekiToYear(item.builtYear);
    if (year == null) return false;
    const cutoff = new Date().getFullYear() - filters.builtWithinYears;
    if (year < cutoff) return false;
  }

  if (filters.facilities.length > 0) {
    const itemFacilities = parseFacilities(item.facilities);
    if (!filters.facilities.every((f) => itemFacilities.includes(f))) return false;
  }

  if (filters.walkWithinMinutes != null) {
    const minutes = parseWalkMinutes(item.access);
    if (minutes == null || minutes > filters.walkWithinMinutes) return false;
  }

  return true;
}

export function SearchableDanchiGrid({
  items,
  linkPrefix,
  showPrefecture,
  showNoVacancy,
  initialQuery = "",
  detailed,
  enableFilters,
}: Props) {
  const [query, setQuery] = useState(initialQuery);
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);

  const filtered = items.filter((item) => {
    if (query && !matchesQuery(item, query)) return false;
    if (enableFilters && !matchesFilters(item, filters)) return false;
    return true;
  });

  return (
    <>
      <SearchBar defaultValue={initialQuery} onChange={setQuery} />

      {enableFilters && (
        <FilterPanel filters={filters} onChange={setFilters} />
      )}

      {filtered.length === 0 ? (
        <p className="text-muted text-sm">
          {query
            ? `「${query}」に一致する物件が見つかりませんでした。`
            : "条件に一致する物件がありません。"}
        </p>
      ) : (
        <>
          {(query || (enableFilters && filters !== INITIAL_FILTERS)) && (
            <p className="text-muted text-xs mb-4">
              {filtered.length}件の物件が見つかりました
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((item) => (
              <Link key={item.id} href={buildLink(item, linkPrefix)}>
                <Card
                  className={`hover:border-accent transition-colors cursor-pointer ${
                    showNoVacancy && item.vacancyCount === 0 ? "opacity-60" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h2 className={detailed ? "text-lg font-bold" : "font-semibold"}>{item.name}</h2>
                    <div className="flex items-center gap-2 shrink-0">
                      {item.vacancyCount > 0 ? (
                        <Badge variant="vacancy">
                          募集中戸数: {item.vacancyCount}
                        </Badge>
                      ) : showNoVacancy ? (
                        <Badge variant="no-vacancy">空無し</Badge>
                      ) : null}
                      {detailed && item.lastCrawledAt && (
                        <span className="text-muted text-xs whitespace-nowrap">
                          最終更新: {new Date(item.lastCrawledAt).toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" })}
                        </span>
                      )}
                    </div>
                  </div>
                  {showPrefecture && item.prefectureName && (
                    <p className="text-accent text-xs font-medium mb-2">
                      {item.prefectureName}
                    </p>
                  )}
                  {item.address && (
                    <p className="text-sm mb-1">
                      <span className="text-muted">住所：</span>{item.address}
                    </p>
                  )}
                  {(item.rentMin || item.rentMax) && (
                    <p className="text-sm mb-1">
                      <span className="text-muted">{rentLabel(item.housingType)}：</span>
                      {item.rentMin
                        ? `${item.rentMin.toLocaleString()}円`
                        : ""}
                      {item.rentMin && item.rentMax ? "〜" : ""}
                      {item.rentMax
                        ? `${item.rentMax.toLocaleString()}円`
                        : ""}
                    </p>
                  )}
                  {detailed && item.builtYear && (
                    <p className="text-sm mb-1">
                      <span className="text-muted">建築年：</span>{item.builtYear}
                    </p>
                  )}
                  {item.access && (
                    <div className="mt-2">
                      {detailed && <p className="text-muted text-xs font-semibold mb-1">交通</p>}
                      <p className="text-xs text-muted">{item.access}</p>
                    </div>
                  )}
                  {detailed && item.facilities && (
                    <div className="mt-2">
                      <p className="text-muted text-xs font-semibold mb-1">設備</p>
                      <p className="text-xs text-muted">{item.facilities}</p>
                    </div>
                  )}
                </Card>
              </Link>
            ))}
          </div>
        </>
      )}
    </>
  );
}
